import {
    DossierDetailType,
    DossierStatusEnum,
    EstablishmentCategory,
    RolesEnum,
    Session,
    establishementStatusEnum,
} from "../../../../types";
import { useLocalStorage } from "../../../../hooks";
import { dossierStorageKey, sessionStorageKey } from "../../../../consts";
import OverViewDossierClient from "./OverViewDossierClient";
import OverViewDossierInstructor from "./OverViewDossierInstructor";
import OverViewDossierAdmin from "./OverViewDossierAdmin";
import OverViewDossierSynerpa from "./OverViewDossierSynerpa";
import {
    canShowDownloadCertificateBtn,
    computeLinearProgressValue,
    getDossierStatusColorByUserRole,
    getValidatorDecisionInfos,
} from "../utils";
import { getValidatorDecision } from "./utils";
import { groupBy } from "../../../../helpers";
import { useDossier } from "../../useDossier";
import { useCallback, useState } from "react";
import { updateDossier } from "../../../../services";

export type ValidatorDecisionInfosType = {
    assignedValidatorDecisionAt: string;
    expirationCertificateAt: string;
};

type OverViewDossierWrapperProps = {
    dossierDetail: DossierDetailType;
    instructorList?: any[];
    toBulkAssignedDossier?: Function;
    downloadCertificate?: Function;
    regionList: EstablishmentCategory[];
};

export default function OverViewDossierWrapper({
    dossierDetail,
    toBulkAssignedDossier,
    instructorList,
    downloadCertificate,
    regionList,
}: OverViewDossierWrapperProps) {
    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        [dossierStorageKey]: { establishmentStatus },
        auth: { user: currentUser, currentUserRoleId: userRoleId },
    } = session;

    const { establishmentGroups } = useDossier();

    const dossierId = dossierDetail.id;

    const [isPayed, setIsPayed] = useState<boolean>(
        dossierDetail?.payment ? dossierDetail?.payment : false
    );

    const linearProgressValueCalculated = [
        RolesEnum.CLIENT,
        RolesEnum.ADMIN_RESEAU,
        RolesEnum.ADMIN_REGION,
    ].includes(userRoleId)
        ? dossierDetail.commitmentsCompleted
        : computeLinearProgressValue(
              userRoleId,
              dossierDetail.referenciel.commitments || []
          );

    const _dossierStatusColors = getDossierStatusColorByUserRole(
        userRoleId,
        dossierDetail
    );

    const showDownloadCertificateBtn = canShowDownloadCertificateBtn(
        dossierDetail,
        establishmentStatus
    );

    const validatorDecision = getValidatorDecision(
        dossierDetail,
        establishmentStatus
    );

    const establishementsGroupsGroupedById = groupBy(
        establishmentGroups,
        (e: EstablishmentCategory) => e.id
    );

    const getGroupById = (groupId: number): EstablishmentCategory | null => {
        if (
            establishementsGroupsGroupedById &&
            establishementsGroupsGroupedById[groupId]
        ) {
            return establishementsGroupsGroupedById[groupId][0];
        }
        return null;
    };

    const establishmentGroup = getGroupById(
        Number(dossierDetail.establishment.establishmentGroup)
    );

    const handlePayment = useCallback(async () => {
        if (dossierId) {
            const payment = !isPayed;
            const updatingDossierRes = await updateDossier(dossierId, {
                payment,
            });
            if (!updatingDossierRes.detail) {
                // handle when updating dossier status
            }

            setIsPayed(payment);
        }
    }, [isPayed]);

    const disablePaymentBtn = ![RolesEnum.ADMIN].includes(userRoleId);

    const disablePaymentBtnForAdmin = [
        DossierStatusEnum.FINALISE,
        DossierStatusEnum.ATTENTE_DECISION,
    ].includes(dossierDetail?.dossierStatus?.code as any);

    const validatorDecisionInfos = getValidatorDecisionInfos(dossierDetail);

    const isQualifed =
        validatorDecision?.code === establishementStatusEnum.QUALIFIE;

    const region = regionList.find(
        (r: EstablishmentCategory) =>
            r.id === dossierDetail.establishment.region
    );

    const institutionalAffiliation = {
        network: establishmentGroup ? establishmentGroup.name : "",
        region: region ? region.name : "",
    };

    const buildOverviewDossierInfos = (dossierDetail: DossierDetailType) => {
        switch (userRoleId) {
            case RolesEnum.ADMIN_REGION:
            case RolesEnum.ADMIN_RESEAU:
            case RolesEnum.CLIENT:
                return (
                    <OverViewDossierClient
                        _dossierStatusColors={_dossierStatusColors}
                        dossierDetail={dossierDetail}
                        linearProgressValue={dossierDetail.commitmentsCompleted}
                        disablePaymentBtn={disablePaymentBtn}
                        downloadCertificate={downloadCertificate}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                        validatorDecision={validatorDecision}
                        isPayed={isPayed}
                        validatorDecisionInfos={validatorDecisionInfos}
                        isQualifed={isQualifed}
                        showTitle={[
                            RolesEnum.ADMIN_RESEAU,
                            RolesEnum.ADMIN_REGION,
                        ].includes(userRoleId)}
                        institutionalAffiliation={institutionalAffiliation}
                    />
                );
            case RolesEnum.ADMIN:
                return (
                    <OverViewDossierAdmin
                        dossierDetail={dossierDetail}
                        instructorList={instructorList}
                        toBulkAssignedDossier={toBulkAssignedDossier}
                        linearProgressValue={linearProgressValueCalculated}
                        validatorDecision={validatorDecision}
                        downloadCertificate={downloadCertificate}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                        onPaymentClick={handlePayment}
                        disablePaymentBtn={disablePaymentBtnForAdmin}
                        isPayed={isPayed}
                        institutionalAffiliation={institutionalAffiliation}
                    />
                );
            case RolesEnum.INSTRUCTEUR:
                return (
                    <OverViewDossierInstructor
                        dossierDetail={dossierDetail}
                        linearProgressValue={linearProgressValueCalculated}
                        disablePaymentBtn={disablePaymentBtn}
                        isPayed={isPayed}
                        institutionalAffiliation={institutionalAffiliation}
                    />
                );
            case RolesEnum.SYNERPA:
                return (
                    <OverViewDossierSynerpa
                        dossierDetail={dossierDetail}
                        linearProgressValue={linearProgressValueCalculated}
                        _dossierStatusColors={_dossierStatusColors}
                        downloadCertificate={downloadCertificate}
                        showDownloadCertificateBtn={showDownloadCertificateBtn}
                        validatorDecision={validatorDecision}
                        establishmentGroup={establishmentGroup}
                        disablePaymentBtn={disablePaymentBtn}
                        isPayed={isPayed}
                        institutionalAffiliation={institutionalAffiliation}
                    />
                );
            default:
                return <></>;
        }
    };

    return buildOverviewDossierInfos(dossierDetail);
}
